// angular
import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, signal } from '@angular/core';
import { catchError, map, of, tap } from 'rxjs';
// interfaces
import {
  ICommentData,
  ICommentForm,
  IReportCommentPayload,
  ICommentResponse,
} from '@app/shared/interfaces/comment.interface';
// services
import { PwjToastService } from './app-toast.service';

export type commentReplay = {
  id?: string | null;
  user?: string | null;
};

@Injectable()
export class CommentsService {
  public replayId!: string | null;

  // states
  public commentReplay = signal<commentReplay>({});
  public commentsData = signal<ICommentData[]>([]);
  public userCommentsData = signal<ICommentData[]>([]);
  public userCommentsDataLoading = signal<boolean>(false);

  // dependencies
  private readonly _httpClient = inject(HttpClient);
  private readonly _toastService = inject(PwjToastService);

  constructor() {
    // set comment replay id

    effect(() => {
      this.replayId = this.commentReplay()?.id as string;
    });
  }

  private _postComment(comment: ICommentForm) {
    const payload: ICommentForm = {
      ...comment,
      ...(this.replayId ? { parent_id: this.replayId } : {}),
    };

    return this._httpClient.post('main/comments', payload);
  }

  private _removeComment(id: string) {
    return this._httpClient.delete(`main/comments/${id}`);
  }

  private _reportComment(payload: IReportCommentPayload) {
    return this._httpClient.post('main/comments/report', payload);
  }

  public sendCommentForm(data: ICommentForm) {
    return this._postComment(data).pipe(
      tap(() => {
        // success message (toast)
        this._toastService.success(
          'toastMessages.submitComment',
          'toastMessages.successTitle'
        );
        this.commentReplay.set({
          id: null,
          user: null,
        });
      }),
      map(() => true),
      catchError(error => {
        // auth error message (toast)
        if (error.status === 401) {
          this._toastService.error(
            'toastMessages.loginMessage',
            'toastMessages.loginTitle'
          );
        } else {
          this._toastService.error(
            'toastMessages.error',
            'toastMessages.errorTitle'
          );
        }
        return of(false);
      })
    );
  }

  public getAllComments(section: string, id: string) {
    return this._httpClient
      .get<ICommentResponse>(`main/comments/${section}/${id}`)
      .subscribe({
        next: res => {
          // function to customize nested comments properties data within the comments object & replies array
          const customizeCommentData = (
            data: ICommentData[]
          ): ICommentData[] => {
            if (data?.length > 0) {
              return data?.map((item: ICommentData) => ({
                ...item,
                created_at:
                  item?.created_at && new Date(item?.created_at).toDateString(),
                user: item.user
                  ? {
                      ...item.user,
                    }
                  : null,
                replies: item?.replies && customizeCommentData(item?.replies),
              }));
            } else {
              return [];
            }
          };

          // customize comments data
          const comments = res.data && customizeCommentData(res.data);
          this.commentsData.set(comments);
        },
        error: err => {},
        complete: () => {},
      });
  }

  public getUserComments() {
    // set loading state to true
    this.userCommentsDataLoading.set(true);

    return this._httpClient
      .get<ICommentResponse>(`main/people/my-comments`)
      .subscribe({
        next: res => {
          // function to customize nested comments properties data within the comments object & replies array
          const customizeCommentData = (
            data: ICommentData[]
          ): ICommentData[] => {
            if (data?.length > 0) {
              return data?.map((item: ICommentData) => ({
                ...item,
                created_at:
                  item?.created_at && new Date(item?.created_at).toDateString(),
                user: {
                  ...item.user,
                },
                replies: item?.replies && customizeCommentData(item?.replies),
              }));
            } else {
              return [];
            }
          };

          // customize comments data
          const comments = res && customizeCommentData(res.data);
          this.userCommentsData.set(comments);
        },
        error: err => {
          this.userCommentsDataLoading.set(false);
        },
        complete: () => {
          this.userCommentsDataLoading.set(false);
        },
      });
  }

  public removeCommentByUser(commentId: string) {
    this._removeComment(commentId).subscribe({
      next: () => {
        this._toastService.success(
          'toastMessages.deleteCommentSuccessfully',
          'toastMessages.successTitle'
        );
      },
      error: error => {
        // auth error message (toast)
        if (error.status === 401) {
          this._toastService.error(
            'toastMessages.loginMessage',
            'toastMessages.loginTitle'
          );
        } else {
          this._toastService.error(
            'toastMessages.error',
            'toastMessages.errorTitle'
          );
        }
      },
      complete: () => {},
    });
  }

  public reportComment(commentId: string, text: string) {
    return this._reportComment({ comment_id: commentId, text }).pipe(
      tap(() => {
        this._toastService.success(
          'toastMessages.reportComment',
          'toastMessages.successTitle'
        );
      }),
      map(() => true),
      catchError(error => {
        if (error.status === 401) {
          this._toastService.error(
            'toastMessages.loginMessage',
            'toastMessages.loginTitle'
          );
        } else {
          this._toastService.error(
            'toastMessages.error',
            'toastMessages.errorTitle'
          );
        }
        return of(false);
      })
    );
  }
}
